import {
  Calendar,
  User,
  Star,
  UserCircle,
  Users,
  Compass,
  Heart,
  Award,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { Character } from "@/routes/innovation-days-demo/types";

// Role-specific icon and styling - unified primary color palette
const getRoleConfig = (role: string) => {
  const roleLower = role.toLowerCase();

  if (roleLower === "protagonist") {
    return {
      icon: Star,
      bg: "#EEF2FF", // primary-25
      text: "#6366F1", // primary-500 - highest saturation
      label: "Protagonist",
    };
  }
  if (roleLower === "deuteragonist") {
    return {
      icon: UserCircle,
      bg: "#EEF2FF", // primary-25
      text: "#7884F7", // primary-400 - medium saturation
      label: "Deuteragonist",
    };
  }
  if (roleLower === "foil") {
    return {
      icon: Users,
      bg: "#EEF2FF", // primary-25
      text: "#7884F7", // primary-400
      label: "Foil",
    };
  }
  if (roleLower === "mentor") {
    return {
      icon: Award,
      bg: "#EEF2FF", // primary-25
      text: "#7884F7", // primary-400
      label: "Mentor",
    };
  }
  if (roleLower === "guide") {
    return {
      icon: Compass,
      bg: "#EEF2FF", // primary-25
      text: "#7884F7", // primary-400
      label: "Guide",
    };
  }
  if (roleLower === "love interest") {
    return {
      icon: Heart,
      bg: "#EEF2FF", // primary-25
      text: "#7884F7", // primary-400
      label: "Love Interest",
    };
  }

  // Default for other roles
  return {
    icon: Award,
    bg: "#EEF2FF", // primary-25
    text: "#7884F7", // primary-400
    label: role.charAt(0).toUpperCase() + role.slice(1),
  };
};

interface CharactersContentProps {
  characters: Character[];
  isExpanded?: boolean;
}

export function CharactersContent({
  characters,
  isExpanded = false,
}: CharactersContentProps) {
  const displayedCharacters = isExpanded ? characters : characters.slice(0, 5);

  return (
    <div className="space-y-8">
      {displayedCharacters.map((c, index) => {
        const roleConfig = c.role ? getRoleConfig(c.role) : null;
        const RoleIcon = roleConfig?.icon;

        return (
          <div key={c.name} className="relative pl-6">
            {/* Subtle divider between characters (except first) */}
            {index > 0 && (
              <div
                className="absolute -top-4 left-0 right-0 h-px"
                style={{ background: "#E4EAF1" }}
              />
            )}

            {/* Left accent bar colored by role */}
            {roleConfig && (
              <div
                className="absolute left-0 top-0 bottom-0 w-1 rounded-full"
                style={{ background: roleConfig.text }}
              />
            )}

            {/* Character header */}
            <div className="mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h4 className="text-[18px] leading-[24px] font-semibold text-[#0F172A]">
                    {c.name}
                  </h4>
                  {c.role && roleConfig && RoleIcon && (
                    <span className="inline-flex items-center gap-1">
                      <RoleIcon
                        className="w-3 h-3"
                        style={{ color: roleConfig.text }}
                      />
                      <span
                        className="text-[12px] leading-[16px] font-medium uppercase"
                        style={{ color: roleConfig.text }}
                      >
                        {roleConfig.label}
                      </span>
                    </span>
                  )}
                </div>

                {/* Meta row: badges and aliases */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {c.age && (
                    <Badge className="w-auto bg-[#F1F5F9] text-foreground rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {c.age.charAt(0).toUpperCase() + c.age.slice(1)}
                      </span>
                    </Badge>
                  )}
                  {c.pronouns && (
                    <Badge className="w-auto bg-[#F1F5F9] text-foreground rounded-full px-[10px] py-1 border-transparent text-[12px] leading-[16px] font-medium flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      <span>{c.pronouns}</span>
                    </Badge>
                  )}
                  {c.aliases && c.aliases.length > 0 && (
                    <span
                      className="text-[13px] leading-[20px] italic"
                      style={{ color: "#64748B" }}
                    >
                      aka &ldquo;{c.aliases.join(", ")}&rdquo;
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Character description */}
            <p
              className="text-[15px] leading-[22px]"
              style={{ color: "#475569" }}
            >
              {c.description}
            </p>
          </div>
        );
      })}
    </div>
  );
}
